import { useMemo } from "react";
import { PopoverOrigin } from "@mui/material";
import {
  DataTableRowData,
  FormatCellObject,
  UseDataTableValuesArgs,
} from "./types";
import { decodeHtmlEntities } from "../../utils";

// take4 row data and format object and return formatted row data
export const formatDataTableRow = (
  row: DataTableRowData,
  formatOptions?: FormatCellObject
) => {
  return Object.entries(row).reduce((formattedRow, [key, value]) => {
    const formatFunction = formatOptions?.[key];
    formattedRow[key] =
      (formatFunction ? formatFunction(value, row) : value) ?? null;
    return formattedRow;
  }, {} as DataTableRowData);
};

// return "-" if value is null or empty string
export const formatNullCellValue = (value: any) => {
  const displayValue = !!!value || value === "" ? "-" : value;
  // Decode HTML entities if value is a string
  return typeof displayValue === "string" ? decodeHtmlEntities(displayValue) : displayValue;
};

export const dataTableResponsiveWidth = { xs: "95%", md: "96%", lg: "98%" };

export const getTableInputAnchorOrigin = (): PopoverOrigin => {
  return window.innerWidth < 600
    ? {
        vertical: "top",
        horizontal: "center",
      }
    : {
        vertical: "bottom",
        horizontal: "left",
      };
};

export const getTableInputTransformOrigin = (): PopoverOrigin => {
  return window.innerWidth < 600
    ? {
        vertical: "bottom",
        horizontal: "center",
      }
    : {
        vertical: "top",
        horizontal: "left",
      };
};

// useDataTableValues hook to memoize and format core variables needed for the data table
export const useDataTableValues = ({
  getKey,
  data,
  disabledRows,
  selectedRows,
}: UseDataTableValuesArgs) => {
  const selectedRowsSet = useMemo(() => new Set(selectedRows), [selectedRows]);

  const allRowIds = useMemo(
    () => data.map((row) => getKey(row) as string),
    [data, getKey]
  );

  const disabledRowsSet = useMemo(() => {
    if (!disabledRows) {
      return new Set();
    }
    return new Set(disabledRows);
  }, [disabledRows]);

  const isAllSelected = useMemo(() => {
    return data.length > 0 && selectedRowsSet.size === data.length;
  }, [data, selectedRowsSet]);

  const headers = useMemo(() => Object.keys(data[0] ?? {}), [data]);
  

  return {
    allRowIds,
    disabledRowsSet,
    isAllSelected,
    selectedRowsSet,
    headers,
  };
};
